/**
 * Data layer for `branch-network-partner-postback`. Thin typed fetch wrappers over
 * /api/v1/branch-network-partner-postback. The BE accepts partner-network postback
 * events for deep-link attribution and routes them to a tenant's branch. Raw
 * `fetch()` is confined to this file per the dash contract.
 *
 * Note: "Branch" in the slug refers to an internal tenant branch routing concept,
 * NOT the external MMP brand. Partner-facing copy uses "Deep Link Network" /
 * "Mobile Attribution Vendor".
 */

export type PostbackEventType = "conversion" | "click" | "install" | "purchase" | string;

export interface PostbackEventRow {
	id: number;
	tenant_id: string;
	partner_id: string;
	branch_id: string;
	event_id: string;
	event_type: PostbackEventType;
	payload_json: string;
	created_at: number;
}

export interface ListPostbackEventsResponse {
	items: PostbackEventRow[];
}

export interface CreatePostbackEventBody {
	event_id: string;
	partner_id: string;
	branch_id: string;
	event_type?: PostbackEventType;
	[extra: string]: unknown;
}

export interface CreatePostbackEventResponse {
	ok: boolean;
	event_id: string;
	partner_id: string;
	branch_id: string;
	event_type: PostbackEventType;
	created_at: number;
}

export interface ListPostbackEventsParams {
	partner_id?: string;
	event_type?: PostbackEventType;
}

/**
 * Template for the postback URL partners call. This is a client-side model used
 * by the config UI to preview the URL a vendor would POST to; the actual URL is
 * fixed server-side but rendered with tenant/partner tokens substituted.
 */
export interface PostbackTemplate {
	base_url: string;
	partner_id: string;
	event_type: PostbackEventType;
	branch_id: string;
	/** Mapping of partner placeholder → our payload field. */
	mapping: Record<string, string>;
}

const BASE = "/api/v1/branch-network-partner-postback";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listEvents(
	params: ListPostbackEventsParams = {},
): Promise<ListPostbackEventsResponse> {
	const qs = new URLSearchParams();
	if (params.partner_id) qs.set("partner_id", params.partner_id);
	if (params.event_type) qs.set("event_type", params.event_type);
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPostbackEventsResponse>(res);
}

export async function createEvent(
	body: CreatePostbackEventBody,
): Promise<CreatePostbackEventResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreatePostbackEventResponse>(res);
}

/**
 * Client-side: renders a postback URL template by substituting {tokens}. Pure,
 * no network. Unknown tokens are left as-is so the partner sees they are
 * missing. Used by the config UI to show a live preview.
 */
export function renderPostbackTemplate(
	template: PostbackTemplate,
	values: Record<string, string> = {},
): string {
	const url = new URL(template.base_url, "https://example.local");
	url.searchParams.set("partner_id", template.partner_id);
	url.searchParams.set("branch_id", template.branch_id);
	url.searchParams.set("event_type", template.event_type);
	for (const [placeholder, field] of Object.entries(template.mapping)) {
		const v = values[field] ?? `{${placeholder}}`;
		url.searchParams.set(placeholder, v);
	}
	// Return pathname + query so tests don't depend on a fake origin.
	return `${url.pathname}${url.search}`;
}

export const branchNetworkPartnerPostbackQueryKeys = {
	all: ["branch-network-partner-postback"] as const,
	list: (params: ListPostbackEventsParams = {}) =>
		[
			...["branch-network-partner-postback"],
			"list",
			params.partner_id ?? "",
			params.event_type ?? "",
		] as const,
	detail: (id: string) =>
		[...["branch-network-partner-postback"], id] as const,
};
